import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials for user auth
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true
});

// Create separate axios instance for admin (no credentials needed, uses header tokens)
const adminClient = axios.create({
  baseURL: API,
  withCredentials: false
});

// Add interceptor to include admin token in all admin requests
adminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers['X-Admin-Token'] = token;
  }
  return config;
});

// Auth APIs
export const exchangeSession = async (sessionId) => {
  const response = await apiClient.post('/auth/session', { session_id: sessionId });
  return response.data;
};

export const getCurrentUser = async () => {
  try {
    const response = await apiClient.get('/auth/me');
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      return null;
    }
    throw error;
  }
};

export const logout = async () => {
  const response = await apiClient.post('/auth/logout');
  return response.data;
};

// Lead APIs
export const createLead = async (leadData) => {
  const response = await apiClient.post('/leads', leadData);
  return response.data;
};

export const getLeadByEmail = async (email) => {
  try {
    const response = await apiClient.get(`/leads/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getLeadById = async (leadId) => {
  try {
    const response = await apiClient.get(`/leads/id/${leadId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Progress APIs
export const getUserProgress = async (leadId) => {
  const response = await apiClient.get(`/progress/${leadId}`);
  return response.data;
};

export const updateProgress = async (progressData) => {
  const response = await apiClient.post('/progress', progressData);
  return response.data;
};

export const submitQuiz = async (quizData) => {
  const response = await apiClient.post('/progress/quiz', quizData);
  return response.data;
};

// Scenario APIs
export const recordScenarioAttempt = async (attemptData) => {
  const response = await apiClient.post('/scenarios/attempt', attemptData);
  return response.data;
};

export const getScenarioStats = async (leadId) => {
  const response = await apiClient.get(`/scenarios/stats/${leadId}`);
  return response.data;
};

// Certificate APIs
export const generateCertificate = async (leadId) => {
  const response = await apiClient.post('/certificate/generate', { lead_id: leadId });
  return response.data;
};

export const verifyCertificate = async (certificateNumber) => {
  try {
    const response = await apiClient.get(`/certificate/${certificateNumber}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getUserCertificate = async (leadId) => {
  try {
    const response = await apiClient.get(`/certificate/user/${leadId}`);
    return response.data;
  } catch (error) {
    return null;
  }
};

// Stats API
export const getOverviewStats = async () => {
  const response = await apiClient.get('/stats/overview');
  return response.data;
};

// Lesson Progress APIs
export const saveLessonProgress = async (userId, moduleId, lessonId) => {
  const response = await apiClient.post('/progress/lesson', {
    user_id: userId,
    module_id: moduleId,
    lesson_id: lessonId,
    completed: true
  });
  return response.data;
};

export const getUserLessonProgress = async (userId) => {
  const response = await apiClient.get(`/progress/lessons/${userId}`);
  return response.data;
};

export const getModuleLessonProgress = async (userId, moduleId) => {
  const response = await apiClient.get(`/progress/lessons/${userId}/${moduleId}`);
  return response.data;
};

// Support APIs
export const submitSupportInquiry = async (inquiryData) => {
  const response = await apiClient.post('/support/inquiry', inquiryData);
  return response.data;
};

// Newsletter API
export const subscribeNewsletter = async (email) => {
  const response = await apiClient.post('/newsletter/subscribe', { email });
  return response.data;
};

// Admin APIs
export const adminLogin = async (password) => {
  const response = await adminClient.post('/admin/login', { password });
  // Store token in localStorage for subsequent requests
  if (response.data.token) {
    localStorage.setItem('admin_token', response.data.token);
  }
  return response.data;
};

export const adminVerify = async () => {
  try {
    const response = await adminClient.get('/admin/verify');
    return response.data;
  } catch (error) {
    return { authenticated: false };
  }
};

export const adminLogout = async () => {
  const response = await adminClient.post('/admin/logout');
  localStorage.removeItem('admin_token');
  return response.data;
};

export const getAdminDashboard = async () => {
  const response = await adminClient.get('/admin/dashboard');
  return response.data;
};

export const getAdminUsers = async (skip = 0, limit = 50) => {
  const response = await adminClient.get(`/admin/users?skip=${skip}&limit=${limit}`);
  return response.data;
};

export const getAdminLeads = async (skip = 0, limit = 50) => {
  const response = await adminClient.get(`/admin/leads?skip=${skip}&limit=${limit}`);
  return response.data;
};

export const getAdminInquiries = async (status = null, skip = 0, limit = 50) => {
  const url = status 
    ? `/admin/inquiries?status=${status}&skip=${skip}&limit=${limit}`
    : `/admin/inquiries?skip=${skip}&limit=${limit}`;
  const response = await adminClient.get(url);
  return response.data;
};

export const updateInquiryStatus = async (inquiryId, status) => {
  const response = await adminClient.patch(`/admin/inquiries/${inquiryId}?status=${status}`);
  return response.data;
};

export const getAdminProgressOverview = async () => {
  const response = await adminClient.get('/admin/progress/overview');
  return response.data;
};

export const getAdminCertificates = async (skip = 0, limit = 50) => {
  const response = await adminClient.get(`/admin/certificates?skip=${skip}&limit=${limit}`);
  return response.data;
};

// Admin Email APIs
export const getAdminEmails = async (template = null, skip = 0, limit = 50) => {
  const url = template 
    ? `/admin/emails?template=${template}&skip=${skip}&limit=${limit}`
    : `/admin/emails?skip=${skip}&limit=${limit}`;
  const response = await adminClient.get(url);
  return response.data;
};

export const sendProgressReminder = async (userId) => {
  const response = await adminClient.post(`/admin/emails/send-reminder?user_id=${userId}`);
  return response.data;
};

export const sendDeadlineAlert = async (userId, daysRemaining = 7) => {
  const response = await adminClient.post(`/admin/emails/send-deadline-alert?user_id=${userId}&days_remaining=${daysRemaining}`);
  return response.data;
};

// Enrollment API
export const getEnrollmentInfo = async (userId) => {
  try {
    const response = await apiClient.get(`/enrollment/${userId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Certificate Eligibility API
export const checkCertificateEligibility = async (leadId) => {
  try {
    const response = await apiClient.get(`/certificate/eligibility/${leadId}`);
    return response.data;
  } catch (error) {
    return null;
  }
};
